// REACT
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// MATERIAL-UI
import { Grid, Typography, useTheme } from "@mui/material";

// COMPONENTS
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import { BottomDrawer } from "../dropdown/BottomDrawer";
import Dropdown from "../dropdown/Dropdown";
import DropdownButton from "../dropdown/DropdownButton";
import DepositCryptoModal from "../Modal/deposit/DepositCryptoModal";

// ASSETS
import GA from "src/analytics/googleAnalytics";
import DepositIcon from "src/icons/DepositIcon";

// CONSTANTS
import { CRYPTO_DENOMINATIONS, FIAT_CURRENCIES } from "src/utils/constants";

// HOOKS
import useDropdown from "src/utils/hooks/useDropdown";

// STYLES
import { WalletOptionStyles } from "../common/styles/WalletOptions.styles";

// REDUX
import { RootState, useAppDispatch, useAppSelector } from "src/store/store";
import api from "src/store/features/api";
import { useDispatch } from "react-redux";
import { useGetUserDatav2Query } from "src/store/features/api/generated";
import { CryptoWithdrawalModal } from "../dropdown/CryptoWithdrawalModal";
import {
  setIsCryptoWithdrawalModalOpen,
  setIsFiatWithdrawalModalOpen,
} from "src/store/features/withdrawal.slice";
import HideLowBalances from "../HideLowBalances";
import { useBankDetails } from "src/utils/hooks/banking/useBankDetails";
import { FiatWithdrawalModal } from "../dropdown/FiatWithdrawalModal";
import { HoldingRes } from "src/store/features/api/generated";

interface WalletOptionsProps {
  hideLowHoldings: (event: React.ChangeEvent<HTMLInputElement> | null, checked: boolean) => void;
  defaultCurrencyHolding?: HoldingRes;
}

export function WalletOptionsDesktop({
  hideLowHoldings,
  defaultCurrencyHolding,
}: WalletOptionsProps) {
  const classes = WalletOptionStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const dropdown = useDropdown();
  const { selectedCurrency } = useAppSelector((state: RootState) => state.settings);

  const { bankAccounts, isInitialLoading } = useBankDetails();
  const { data: userData } = useGetUserDatav2Query();

  const { data: cryptoAddresses = [] } = api.useGetUserCryptoAddressesQuery({ onlyVerified: true });
  const { data: allCryptoData } = api.useGetCryptoCurrenciesQuery();
  const theme = useTheme();

  // Filter bank accounts: if subsidiary is 'US', only show USD currency accounts
  const filteredBankAccounts = React.useMemo(() => {
    if (!bankAccounts || !userData) {
      return bankAccounts;
    }
    const subsidiary = userData.subsidiary;
    if (subsidiary === "US" || subsidiary === "6") {
      const filtered = bankAccounts.filter((account) => account.currency === "USD");
      return filtered;
    } else {
      return bankAccounts;
    }
  }, [bankAccounts, userData]);

  const depositData = React.useMemo(() => {
    if (!allCryptoData) return [];
    return allCryptoData?.filter((crypto) => crypto.permissions?.deposit);
  }, [allCryptoData]);

  useEffect(() => {
    if (location.pathname === "/wallets/cryptoWithdraw") {
      dispatch(setIsCryptoWithdrawalModalOpen(true));
      // dispatch(setIsFiatWithdrawalModalOpen(true));
      GA.modal("Withdraw", "open");
    }

    if (location.pathname === "/wallets/fiatWithdraw") {
      dispatch(setIsFiatWithdrawalModalOpen(true));
      GA.modal("Withdraw", "open");
    }
  }, []);

  const depositConfig = {
    subtitle: "Select coin to deposit",
    data: depositData,
    searchable: true,
    onClick: dropdown.handleDepositClick,
    button: {
      title: "Deposit",
      endIcon: <DepositIcon width={"20px"} height={"20px"} color={theme.palette.text.primary} />,
      disabled: false,
      fullWidth: false,
      className: classes.buttonContainer,
      width: "89px",
    },
  };

  return (
    <>
      <Grid
        container
        item
        sx={{
          display: { xs: "none", md: "flex" },
          justifyContent: "flex-end",
        }}>
        <DropdownButton config={depositConfig as any} />
        <Dropdown
          subtitle="Select currency"
          data={[...CRYPTO_DENOMINATIONS, ...FIAT_CURRENCIES]}
          icon={<CountryOrCryptoFlag name={selectedCurrency} size={16} />}
          className={classes.buttonContainer}
          width="68px">
          <Typography className={classes.buttonText}>{selectedCurrency}</Typography>
        </Dropdown>
      </Grid>
      {/* Hide low balances */}
      <HideLowBalances hideLowHoldings={hideLowHoldings} />
      <DepositCryptoModal
        isOpen={dropdown.depositOpen}
        onClose={() => dropdown.setDepositOpen((prev) => !prev)}
        isFbAsset={dropdown.depositToken.depositable}
        token={dropdown.depositToken}
        error={dropdown.depositError}
        isLoading={dropdown.loading}
      />
      <CryptoWithdrawalModal cryptoAddresses={cryptoAddresses} />
      <FiatWithdrawalModal
        bankAccounts={
          filteredBankAccounts?.map((account) => ({
            id: account.trailingAccNumberDigits,
            accountName: `Account ending in ${account.trailingAccNumberDigits}`,
            accountNumber: account.trailingAccNumberDigits,
            bankName: "Bank Account",
            currency: account.currency,
            isDefault: false,
          })) || []
        }
        defaultCurrencyHolding={defaultCurrencyHolding}
      />
    </>
  );
}

export default function WalletOptionsMobile({
  defaultCurrencyHolding,
}: {
  defaultCurrencyHolding?: HoldingRes;
}) {
  const classes = WalletOptionStyles();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const dropdown = useDropdown();

  // const { isWithdrawalModalOpen } = useAppSelector((state) => state.withdrawals);
  const [depositOpen, setDepositOpen] = useState<boolean>(false);
  const { data: rawCryptos = [] } = api.useGetCryptoCurrenciesQuery();
  const { data: cryptoAddresses = [] } = api.useGetUserCryptoAddressesQuery({ onlyVerified: true });
  const { bankAccounts, isInitialLoading } = useBankDetails();
  const { data: userData } = useGetUserDatav2Query();
  const depositData = dropdown.formatDepositData(rawCryptos);

  // Filter bank accounts: if subsidiary is 'US', only show USD currency accounts
  const filteredBankAccounts = React.useMemo(() => {
    if (!bankAccounts || !userData) {
      return bankAccounts;
    }
    const subsidiary = userData.subsidiary;
    if (subsidiary === "US" || subsidiary === "6") {
      const filtered = bankAccounts.filter((account) => account.currency === "USD");
      return filtered;
    } else {
      return bankAccounts;
    }
  }, [bankAccounts, userData]);

  useEffect(() => {
    if (location.pathname === "/wallets/cryptoWithdraw") {
      dispatch(setIsCryptoWithdrawalModalOpen(true));
      // dispatch(setIsFiatWithdrawalModalOpen(true));
      GA.modal("Withdraw", "open");
    }

    if (location.pathname === "/wallets/fiatWithdraw") {
      // dispatch(setIsCryptoWithdrawalModalOpen(true));
      dispatch(setIsFiatWithdrawalModalOpen(true));
      GA.modal("Withdraw", "open");
    }
  }, []);

  React.useEffect(() => {
    if (location.pathname === "/wallets/deposit") {
      setDepositOpen(true);
    }
  }, [location]);

  const handleDepositClose = (event: React.MouseEvent): void => {
    event.stopPropagation();
    setDepositOpen(false);
    GA.modal("Deposit", "close");
  };

  const depositConfig = {
    drawerOpen: depositOpen,
    drawerClose: handleDepositClose,
    subtitle: "Select coin to deposit",
    data: depositData,
    searchable: true,
    onClick: dropdown.handleDepositClick,
    drawerStyles: {
      disabled: false,
      borderRadius: "12px 12px 0px 0px",
      height: "90%",
    },
  };

  return (
    <>
      {depositData && (
        <Grid container item xs={8} spacing={2} className={classes.container}>
          {/* DEPOSIT */}
          <Grid
            item
            onClick={() => setDepositOpen(true)}
            className={classes.iconDescriptionContainer}>
            <Grid item className={classes.cursor}>
              <DepositIcon backgroundColor="#002035" background width={"32px"} height={"32px"} />
            </Grid>
            <Grid item>
              <Typography className={classes.description}>Deposit</Typography>
            </Grid>
            <BottomDrawer config={depositConfig} />
          </Grid>
        </Grid>
      )}

      <CryptoWithdrawalModal cryptoAddresses={cryptoAddresses} />

      <FiatWithdrawalModal
        bankAccounts={
          filteredBankAccounts?.map((account) => ({
            id: account.trailingAccNumberDigits,
            accountName: `Account ending in ${account.trailingAccNumberDigits}`,
            accountNumber: account.trailingAccNumberDigits,
            bankName: "Bank Account",
            currency: account.currency,
            isDefault: false,
          })) || []
        }
        defaultCurrencyHolding={defaultCurrencyHolding}
      />

      <DepositCryptoModal
        isOpen={dropdown.depositOpen}
        onClose={() => dropdown.setDepositOpen((prev) => !prev)}
        isFbAsset={dropdown.depositToken.depositable}
        token={dropdown.depositToken}
        error={dropdown.depositError}
        isLoading={dropdown.loading}
      />
    </>
  );
}
